import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, queryClient, streamSse, useAction, useApi } from '../lib/api';
import { relative } from '../lib/format';
import { useSession } from '../lib/session';
import type { AiStatus, AiThread, TranscriptItem } from '../lib/types';
import { Icons } from '../components/icons';
import { Markdown } from '../components/markdown';
import { Badge, Button, cx, ErrorNote, Spinner, Textarea } from '../components/ui';

interface ToolChip {
  id?: string;
  name: string;
  title: string;
  state: 'running' | 'ok' | 'error';
  error?: string;
}
interface Proposal {
  entryId: string;
  approvalRequestId?: string;
  status: string;
}
interface Msg {
  role: 'user' | 'assistant';
  text: string;
  tools: ToolChip[];
  proposals: Proposal[];
  notices: string[];
  error?: string;
  streaming?: boolean;
}

const SUGGESTIONS = [
  'How much runway do we have, and how has burn trended over the last six months?',
  'Why did operating expenses change last month? Walk me through the biggest drivers.',
  'Which invoices are most overdue, and who should we chase first?',
  'What is still blocking this month’s close?',
  'Are there any anomalies I should review before we close?',
  'Show me MRR movements and net revenue retention for this year.',
];

const fromTranscript = (t: TranscriptItem[]): Msg[] => t.map((m) => ({ role: m.role, text: m.text, tools: (m.tools ?? []).map((x) => ({ ...x, state: 'ok' as const })), proposals: [], notices: [] }));

export function AssistantPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { me } = useSession();
  const status = useApi<AiStatus>('/api/ai/status');
  const threads = useApi<AiThread[]>('/api/ai/threads');
  const [messages, setMessages] = useState<Msg[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // The thread whose messages are on screen. A new chat adopts its id mid-stream; the URL then follows without a reload.
  const shown = useRef<string | undefined>(undefined);
  const abort = useRef<AbortController | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const enabled = status.data?.enabled ?? me.ai.enabled;
  // The assistant spends the company's allowance, so it waits for a confirmed email (the banner resends the link).
  const canSend = enabled && me.user.emailVerified;

  useEffect(() => {
    if (threadId === shown.current) return;
    abort.current?.abort();
    shown.current = threadId;
    setLoadError(null);
    if (!threadId) {
      setMessages([]);
      return;
    }
    setLoadingThread(true);
    api<{ transcript: TranscriptItem[] }>(`/api/ai/threads/${threadId}`)
      .then((t) => shown.current === threadId && setMessages(fromTranscript(t.transcript)))
      .catch((e) => shown.current === threadId && setLoadError(e))
      .finally(() => setLoadingThread(false));
  }, [threadId]);

  useEffect(() => () => abort.current?.abort(), []);

  useEffect(() => {
    const el = scroller.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 240) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const patchLast = (f: (m: Msg) => Msg) => setMessages((ms) => (ms.length ? [...ms.slice(0, -1), f(ms[ms.length - 1]!)] : ms));

  async function send(text: string) {
    const message = text.trim();
    if (!message || busy) return;
    setInput('');
    setBusy(true);
    setMessages((ms) => [...ms, { role: 'user', text: message, tools: [], proposals: [], notices: [] }, { role: 'assistant', text: '', tools: [], proposals: [], notices: [], streaming: true }]);
    requestAnimationFrame(() => scroller.current && (scroller.current.scrollTop = scroller.current.scrollHeight));
    const ctl = new AbortController();
    abort.current = ctl;
    let finished = false;
    try {
      await streamSse(
        '/api/ai/chat',
        { threadId: shown.current, message },
        (event, raw) => {
          const d = raw as Record<string, unknown>;
          switch (event) {
            case 'thread':
              if (shown.current !== d.threadId) {
                shown.current = d.threadId as string;
                navigate(`/assistant/${d.threadId as string}`, { replace: true });
              }
              break;
            case 'text':
              patchLast((m) => ({ ...m, text: m.text + (d.delta as string) }));
              break;
            case 'tool_call':
              patchLast((m) => ({ ...m, tools: [...m.tools, { id: d.id as string, name: d.name as string, title: d.title as string, state: 'running' }] }));
              break;
            case 'tool_result':
              patchLast((m) => ({ ...m, tools: m.tools.map((t) => (t.id === d.id ? { ...t, state: d.ok ? 'ok' : 'error', error: d.error as string | undefined } : t)) }));
              break;
            case 'proposal':
              patchLast((m) => ({ ...m, proposals: [...m.proposals, d as unknown as Proposal] }));
              break;
            case 'notice':
              patchLast((m) => ({ ...m, notices: [...m.notices, d.message as string] }));
              break;
            case 'error':
              patchLast((m) => ({ ...m, error: d.message as string }));
              break;
            case 'done':
              finished = true;
              patchLast((m) => ({ ...m, streaming: false }));
              break;
          }
        },
        ctl.signal,
      );
      if (!finished && !ctl.signal.aborted) patchLast((m) => (m.error ? m : { ...m, error: 'The connection closed before the assistant finished. Try again.' }));
    } catch (e) {
      if (!ctl.signal.aborted) patchLast((m) => ({ ...m, error: e instanceof Error ? e.message : 'The request failed.' }));
    } finally {
      patchLast((m) => ({ ...m, streaming: false }));
      if (abort.current === ctl) abort.current = null;
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: ['/api/ai/threads'] });
      void queryClient.invalidateQueries({ queryKey: ['/api/ai/status'] });
      // Proposals land in the approvals inbox; refresh the badge counts.
      void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/api/approvals') || String(q.queryKey[0]).startsWith('/api/command-center') });
    }
  }

  const stop = () => {
    abort.current?.abort();
    patchLast((m) => ({ ...m, streaming: false, notices: [...m.notices, 'Stopped.'] }));
    setBusy(false);
  };

  const remove = useAction((id: string) => api(`/api/ai/threads/${id}`, { method: 'DELETE' }), { success: 'Conversation deleted' });

  return (
    <div className="glass -mb-6 grid h-[calc(100vh-108px)] grid-cols-[260px_minmax(0,1fr)] overflow-hidden rounded-2xl">
      {/* Threads */}
      <aside className="flex min-h-0 flex-col border-r border-ink/[0.06] bg-white/45">
        <div className="p-3">
          <Button variant="ai" className="w-full justify-center" icon={<Icons.Plus size={15} />} onClick={() => navigate('/assistant')}>
            New conversation
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {threads.isLoading && <Spinner className="mx-auto mt-4 size-4" />}
          {(threads.data ?? []).map((t) => (
            <div key={t.id} className={cx('group flex items-center rounded-md', t.id === threadId ? 'bg-ai-soft' : 'hover:bg-white/70')}>
              <Link to={`/assistant/${t.id}`} className="min-w-0 flex-1 px-2.5 py-2">
                <div className={cx('truncate text-[13px]', t.id === threadId ? 'font-medium text-ai' : 'text-ink')}>{t.title}</div>
                <div className="text-[11px] text-faint">{relative(t.updatedAt)}</div>
              </Link>
              <button
                aria-label="Delete conversation"
                className="mr-1 hidden rounded p-1 text-faint group-hover:block hover:bg-sunken hover:text-bad"
                onClick={() =>
                  remove.mutate(t.id, {
                    onSuccess: () => {
                      if (t.id === threadId) navigate('/assistant');
                    },
                  })
                }
              >
                <Icons.Trash size={14} />
              </button>
            </div>
          ))}
          {threads.data?.length === 0 && <p className="px-2.5 py-2 text-[12px] text-faint">No conversations yet.</p>}
        </div>
        {status.data && (
          <div className="border-t border-line p-3 text-[11.5px] text-muted">
            <div className="flex items-center gap-1.5">
              <span className={cx('size-1.5 rounded-full', status.data.enabled ? 'bg-good' : 'bg-faint')} />
              {status.data.enabled ? `${status.data.provider === 'openai' ? 'OpenAI' : 'Claude'} · ${status.data.model}` : 'Assistant off'}
            </div>
            <div className="mt-0.5">
              {status.data.tools.length} tools · {status.data.tools.filter((t) => t.write).length} propose changes for approval
            </div>
            {status.data.enabled && <UsageLine usage={status.data.usage} />}
          </div>
        )}
      </aside>

      {/* Conversation */}
      <section className="flex min-h-0 flex-col bg-white/15">
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-6 py-8">
            {loadError ? <ErrorNote error={loadError} /> : null}
            {loadingThread && <Spinner className="mx-auto size-5" />}
            {!loadingThread && messages.length === 0 && <Welcome enabled={enabled} status={status.data} onPick={(s) => canSend && void send(s)} />}
            <div className="space-y-6">
              {messages.map((m, i) => (m.role === 'user' ? <UserBubble key={i} text={m.text} /> : <AssistantBubble key={i} m={m} />))}
            </div>
          </div>
        </div>
        <div className="border-t border-ink/[0.06] bg-white/55">
          <form
            className="mx-auto flex max-w-3xl items-end gap-2 px-6 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <Textarea
              rows={1}
              className="max-h-48 min-h-[42px] flex-1 resize-none"
              placeholder={
                !enabled ? 'The assistant is off on this server' : canSend ? 'Ask about your books, or ask the assistant to draft an entry…' : `Confirm your email to use the assistant: open the link we sent to ${me.user.email}`
              }
              disabled={!canSend}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 192)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send(input);
                }
              }}
            />
            {busy ? (
              <Button type="button" onClick={stop} icon={<Icons.X size={15} />}>
                Stop
              </Button>
            ) : (
              <Button type="submit" variant="ai" disabled={!canSend || !input.trim()} icon={<Icons.Send size={15} />}>
                Send
              </Button>
            )}
          </form>
          <p className="mx-auto -mt-2 max-w-3xl px-6 pb-3 text-[11px] text-faint">The assistant is an AI and can be wrong; check what matters. It reads the live ledger with your permissions and can draft entries and accruals, but every change waits for a person to approve it.</p>
        </div>
      </section>
    </div>
  );
}

function UsageLine({ usage }: { usage: AiStatus['usage'] }) {
  const out = usage.used >= usage.limit;
  const resets = new Date(usage.resetsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return (
    <div className={cx('mt-0.5', out && 'text-bad')} title={`Resets ${resets}`}>
      {Math.min(usage.used, usage.limit)} of {usage.limit} messages this month{out && ` · resets ${resets}`}
    </div>
  );
}

function Welcome({ enabled, status, onPick }: { enabled: boolean; status?: AiStatus; onPick: (s: string) => void }) {
  return (
    <div className="animate-fade-up py-6 text-center">
      <span className="mx-auto grid size-11 place-items-center rounded-xl bg-ai-soft text-ai">
        <Icons.Sparkle size={22} />
      </span>
      <h1 className="mt-3 text-[20px] font-semibold">Ask your books anything</h1>
      <p className="mx-auto mt-1 max-w-md text-[13px] text-muted">An AI assistant that answers from the ledger itself, with tool calls you can see. Nothing is posted without approval.</p>
      {enabled ? (
        <div className="mt-6 grid gap-2 text-left sm:grid-cols-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => onPick(s)} className="rounded-xl border border-white/80 bg-white/70 p-3 text-[12.5px] text-ink-2 shadow-card transition-colors hover:border-ai/40 hover:bg-white hover:text-ink">
              {s}
            </button>
          ))}
        </div>
      ) : (
        <div className="mx-auto mt-6 max-w-md rounded-xl border border-white/80 bg-white/70 p-4 text-[12.5px] text-muted shadow-card">
          Set <code className="rounded bg-sunken px-1">OPENAI_API_KEY</code> or <code className="rounded bg-sunken px-1">ANTHROPIC_API_KEY</code> in{' '}
          <code className="rounded bg-sunken px-1">.env</code> or the server environment, then restart the server to turn the assistant on.
        </div>
      )}
      {status && status.tools.length > 0 && (
        <div className="mt-6 flex flex-wrap justify-center gap-1.5">
          {status.tools.map((t) => (
            <Badge key={t.name} tone={t.write ? 'ai' : 'neutral'}>
              {t.title}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand px-4 py-2.5 text-[13.5px] whitespace-pre-wrap text-white">{text}</div>
    </div>
  );
}

function AssistantBubble({ m }: { m: Msg }) {
  const waiting = m.streaming && !m.text && !m.tools.some((t) => t.state === 'running');
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg bg-ai-soft text-ai">
        <Icons.Sparkle size={15} />
      </span>
      <div className="min-w-0 flex-1">
        {m.tools.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {m.tools.map((t, i) => (
              <span
                key={t.id ?? i}
                title={t.error}
                className={cx(
                  'inline-flex h-6 items-center gap-1.5 rounded-full border px-2 text-[11.5px]',
                  t.state === 'error' ? 'border-bad/30 bg-bad-soft text-bad' : t.state === 'running' ? 'border-ai/30 bg-ai-soft text-ai' : 'border-line bg-surface text-ink-2',
                )}
              >
                {t.state === 'running' ? <Spinner className="size-3" /> : t.state === 'error' ? <Icons.Alert size={12} /> : <Icons.Check size={12} />}
                {t.title}
              </span>
            ))}
          </div>
        )}
        {m.text && <Markdown text={m.text} />}
        {waiting && (
          <div className="typing flex gap-1 py-2 text-ai">
            <span className="size-1.5 rounded-full bg-current" />
            <span className="size-1.5 rounded-full bg-current" />
            <span className="size-1.5 rounded-full bg-current" />
          </div>
        )}
        {m.proposals.map((p) => (
          <div key={p.entryId} className="mt-3 flex items-center gap-3 rounded-lg border border-ai/25 bg-ai-soft/60 p-3">
            <Icons.Inbox size={18} className="text-ai" />
            <div className="min-w-0 flex-1 text-[12.5px]">
              <div className="font-medium text-ink">Draft entry waiting for approval</div>
              <div className="text-muted">A reviewer other than the requester must approve it before it posts.</div>
            </div>
            <Link to={p.approvalRequestId ? `/approvals?focus=${p.approvalRequestId}` : `/journal/${p.entryId}`} className="text-[12.5px] font-medium text-ai hover:underline">
              Review →
            </Link>
          </div>
        ))}
        {m.notices.map((n, i) => (
          <p key={i} className="mt-2 text-[12px] text-muted italic">
            {n}
          </p>
        ))}
        {m.error && (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-bad-soft px-3 py-2 text-[12.5px] text-bad">
            <Icons.Alert size={14} /> {m.error}
          </div>
        )}
      </div>
    </div>
  );
}
